<template>
  <div>
    <b-card>
      <b-row
        class="mb-2"
      >
        <b-col
          md="8"
          class="d-flex align-items-center justify-content-start"
        >
          <b-col
            cols="12"
            md="3"
            class="pl-0"
          >
            <v-select
              v-model="query.deskContractId"
              :options="codes.contracts"
              placeholder="계약명"
              :reduce="option => option.code"
            />
          </b-col>

          <b-form-datepicker
            v-model="query.searchStartDate"
            class="el-def"
            placeholder="조회기간(Fr)"
            style="width: 240px"
          />
          <label
            class="ml-50 mr-50"
          >~</label>
          <b-form-datepicker
            v-model="query.searchEndDate"
            class="el-def"
            placeholder="조회기간(To)"
            style="width: 240px"
          />
          <label class="ml-50">(*당일데이터 조회 불가)</label>
        </b-col>

        <b-col
          md="4"
          class="d-flex align-items-center justify-content-end"
        >
          <b-button
            variant="primary"
            class="btn-search"
            @click.prevent="fetchData()"
          >
            조회
          </b-button>
          <b-button
            variant="outline-dark"
            class="btn-search ml-1"
            @click.prevent="excelDownload()"
          >
            <span>엑셀 다운로드</span>
          </b-button>
        </b-col>
      </b-row>

      <detail-grid
        :data="contractStatData"
      />

    </b-card>
  </div>
</template>

<script>
import axios from '@axios'
import {
  ref, watch, onMounted, getCurrentInstance,
} from "@vue/composition-api"
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import dayjs from 'dayjs'
import ExcelJS from "exceljs"
import { saveAs } from "file-saver"
import vSelect from 'vue-select'
import DetailGrid from './grid/ContractDetailGrid.vue'

export default {
  components: {
    DetailGrid,
    vSelect,
  },

  setup() {
    onMounted(() => {
      fetchCodes()
      fetchData()
    })

    const query = ref({
      deskContractId: null,
      searchStartDate: dayjs().add(-1, 'month').format('YYYY-MM-DD'),
      searchEndDate: dayjs().add(-1, 'day').format('YYYY-MM-DD'),
    })

    watch(query.value, () => {
      fetchData()
    })

    const toast = useToast()
    const pushToast = (variant, title) => {
      let icon
      if (variant === 'success') {
        icon = 'CheckCircleIcon'
      } else {
        icon = 'AlertTriangleIcon'
      }
      toast({
        component: ToastificationContent,
        props: { title, icon, variant },
      })
    }

    const contractStatData = ref([])

    const fetchData = () => {
      const frDate = dayjs(query.value.searchStartDate)
      const toDate = dayjs(query.value.searchEndDate)

      if (Math.abs(frDate.diff(toDate, 'day')) > 30) {
        toast({
          component: ToastificationContent,
          props: {
            title: '조회 기간은 최대 30일을 넘을 수 없습니다.',
            icon: 'AlertTriangleIcon',
            variant: 'warning',
          },
        })
        return
      }

      axios.get("/fa/desk-statistics/contract", {
        params: {
          startDate: query.value.searchStartDate ? `${query.value.searchStartDate}T00:00:00` : null,
          endDate: query.value.searchEndDate ? `${query.value.searchEndDate}T23:59:59` : null,
          deskInfoId: query.value.deskContractId,
        },
      })
        .then(rs => {
          const { data } = rs
          contractStatData.value = data
        })
        .catch(() => {
          pushToast('danger', '데이터 조회 중 오류가 발생하였습니다.')
        })
    }

    const codes = ref({
      contentType: [],
      postStatusType: [],
      contracts: [],
      validContracts: [],
    })

    const fetchCodes = () => {
      axios.get('/fa/desk-info/codes')
        .then(rs => {
          const {
            deskInfoContentType,
            postStatusType,
            contracts,
            validContracts,
          } = rs.data
          codes.value.contentType = deskInfoContentType
          codes.value.postStatusType = postStatusType
          codes.value.contracts = contracts
          codes.value.validContracts = validContracts
        })
        .catch(() => {
          pushToast('danger', '코드 데이터를 불러오는데 실패하였습니다.')
        })
    }

    const excelDownload = async () => {
      const workbook = new ExcelJS.Workbook()
      const worksheet = workbook.addWorksheet("sheet1")

      worksheet.addRow()

      worksheet.getCell('A1').value = '계약명'
      worksheet.getCell('B1').value = '상세 코드'
      worksheet.getCell('C1').value = '해당 코드가 등록된 요양기관 수'
      worksheet.getCell('D1').value = '일일팝업 노출 수'
      worksheet.getCell('E1').value = '데스크인포 버튼 클릭 수'

      worksheet.columns = [
        { key: "contractName", width: 15 },
        { key: "entryCode", width: 20 },
        { key: "hcpWorkPlaceCount", width: 15, style: { numFmt: '#,##0' } },
        { key: "dailyPopupCount", width: 15, style: { numFmt: '#,##0' } },
        { key: "buttonPopupCount", width: 15, style: { numFmt: '#,##0' } },
      ]

      const startRow = 3

      const stats = contractStatData.value

      stats.map(x => {
        const record = {
          contractName: x.contractName,
          entryCode: x.entryCode,
          hcpWorkPlaceCount: x.hcpWorkPlaceCount,
          dailyPopupCount: x.dailyPopupCount,
          buttonPopupCount: x.buttonPopupCount,
        }

        worksheet.addRow(record)
      })

      // worksheet.mergeCells(`A${endRow}:B${endRow}`)

      worksheet.eachRow({ includeEmpty: true }, (row, number) => {
        row.eachCell({ includeEmpty: true }, cell => {
          if (number === 1) { // 헤더 스타일 적용
            // eslint-disable-next-line no-param-reassign
            cell.font = {
              bold: true,
            }

            // eslint-disable-next-line no-param-reassign
            cell.fill = {
              type: 'pattern',
              pattern: 'solid',
              fgColor: { argb: 'D3D3D3' },
            }
          }

          // eslint-disable-next-line no-param-reassign
          cell.border = {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' },
          }

          if (cell.value === '소계' || cell.value === '합계') {
            // eslint-disable-next-line no-param-reassign
            cell.alignment = {
              vertical: 'middle',
              horizontal: 'center',
            }
          } else {
            // eslint-disable-next-line no-param-reassign
            cell.alignment = {
              vertical: 'middle',
              horizontal: 'left',
            }
          }
        })
      })

      const mimeType = {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }

      const buffer = await workbook.xlsx.writeBuffer()
      const blob = new Blob([buffer], mimeType)
      saveAs(blob, "데스크인포 계약 통계.xlsx")
    }

    return {
      query,
      contractStatData,
      fetchData,
      excelDownload,
      codes,
    }
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';

.dropdown-menu {
  z-index: 100 !important;
}
</style>
